import './documentUploads.css';
import OptionalContractUpload from './OptionalContractUpload';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GlassCard, GlassSection } from './GlassSurface';
import { GlassCtaButton } from './GlassCtaButton';
import { GlassInput } from './GlassInput';
import { GlassChip } from './GlassChip';
import { GlassSectionHeader } from './GlassSectionHeader';
import { GlassTooltip } from './GlassTooltip';
import { GlassSpinner } from './GlassSpinner';
import { GlassPageIndicator } from './GlassPageIndicator';
import { ImageUploadSection } from './ImageUploadSection';
import { 
  uploadImage,
  createSpace,
  updateSpace,
  fetchSpaceFeatures,
  fetchSpaceRules,
  createMenu
} from '../../Apiservices/Auth/servers/venueServices';
import { CalendarDropdown } from './redesigncatering/CalendarDropdown';
import CustomCalendar from '../../redesign/components/Customcalender';
import { fetchCategoryEvents } from '../../Apiservices/Auth/servers/fetchCategoryEvents';

import './VenueSpaceForm.css';
import './glass-tokens.css';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Space Type Selection
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const SpaceTypeSection = ({ 
  spaceTypes, 
  selectedTypeId, 
  onSelect,
  errors = {}
}) => (
  <div className="space-form-section">
    <label className="section-label">Space Type *</label>
    <div className="space-type-grid">
      {spaceTypes.map(type => (
        <div
          key={type.id}
          className={`space-type-card ${selectedTypeId === type.id ? 'selected' : ''}`}
          onClick={() => onSelect(type.id)}
        >
          <div className="space-type-name">{type.name}</div>
          {type.category && (
            <div className="space-type-category">{type.category.name}</div>
          )}
        </div>
      ))}
    </div>
    {errors.spaceType && <div className="error-text">{errors.spaceType}</div>}
  </div>
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Capacity & Area Section
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const CapacityAreaSection = ({
  minCapacity,
  setMinCapacity,
  maxCapacity,
  setMaxCapacity,
  areaSqft,
  setAreaSqft,
  errors = {}
}) => (
  <div className="space-form-section">
    <label className="section-label">Capacity & Area</label>
    <div className="capacity-row">
      <div className="capacity-field">
        <GlassInput
          label="Min Capacity *"
          placeholder="100"
          type="number"
          value={minCapacity}
          onChange={setMinCapacity}
          error={errors.minCapacity}
        />
      </div>
      <div className="capacity-field">
        <GlassInput
          label="Max Capacity *"
          placeholder="500"
          type="number"
          value={maxCapacity}
          onChange={setMaxCapacity}
          error={errors.maxCapacity}
        />
      </div>
    </div>
    <div className="area-field">
      <GlassInput
        label="Area (sqft) *"
        placeholder="5000"
        type="number"
        value={areaSqft}
        onChange={setAreaSqft}
        error={errors.areaSqft}
      />
    </div>
  </div>
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Menu Item Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Menu Item Component (Final Fix)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const MenuItem = ({ menu, selected, onToggle, isNew = false, onEdit, onDelete }) => {
  const isVeg = menu.isVeg || menu.categoryId === 1;
  const currency = '₹';
  
  // Safely convert price to number with fallback
  const price = typeof menu.price === 'number' 
    ? menu.price 
    : parseFloat(menu.price) || 0;
  
  const discountPrice = typeof menu.discountPrice === 'number' 
    ? menu.discountPrice 
    : parseFloat(menu.discountPrice) || null;

  return (
    <div className={`menu-item ${selected ? 'selected' : ''}`}>
      <div className="menu-item-checkbox">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggle(menu.id || menu)}
        />
      </div>
      <div className="menu-item-content">
        <div className="menu-item-header">
          <div className="menu-item-name-wrapper">
            <span className={`veg-indicator ${isVeg ? 'veg' : 'non-veg'}`}>
              {isVeg ? '🟢' : '🔴'}
            </span>
            <span className="menu-item-name">{menu.name || 'Unnamed Menu'}</span>
            {isNew && (
              <span className="menu-item-new-badge">NEW</span>
            )}
          </div>
          <div className="menu-item-actions">
            {onEdit && (
              <button className="menu-item-edit" onClick={() => onEdit(menu)}>
                ✏️
              </button>
            )}
            {onDelete && (
              <button className="menu-item-delete" onClick={() => onDelete(menu)}>
                🗑️
              </button>
            )}
          </div>
        </div>
        <div className="menu-item-details">
          <span className="menu-item-price">
            {currency}{price.toFixed(0)}/person
          </span>
          {discountPrice !== null && discountPrice > 0 && (
            <span className="menu-item-discount">
              {currency}{discountPrice.toFixed(0)}
            </span>
          )}
          {menu.cuisineCategory && menu.cuisineCategory.name && (
            <span className="menu-item-cuisine">{menu.cuisineCategory.name}</span>
          )}
          {menu.description && (
            <span className="menu-item-description">{menu.description}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Menu Selection Section
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const MenuSelectionSection = ({
  availableMenus,
  selectedMenuIds,
  onToggleMenu,
  onCreateMenu,
  onDeleteMenu,
  cuisineCategories,
  currency = '₹',
  errors = {}
}) => {
  const [showNewMenuDialog, setShowNewMenuDialog] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [newMenus, setNewMenus] = useState([]);

  const handleCreateMenu = async (menuData) => {
    try {
      const result = await onCreateMenu(menuData);
      if (result) {
        setShowNewMenuDialog(false);
      }
    } catch (error) {
      console.error("Error creating menu:", error);
      alert("Failed to create menu");
    }
  };

  const handleEditMenu = (menu) => {
    setEditingMenu(menu);
    setShowNewMenuDialog(true);
  };

  const handleDeleteMenu = async (menu) => {
    if (window.confirm(`Delete "${menu.name}"?`)) {
      try {
        await onDeleteMenu(menu.id);
      } catch (error) {
        console.error("Failed to delete menu:", error);
        const errMsg = error.response?.data?.message || error.message || "Failed to delete menu";
        alert(errMsg);
      }
    }
  };

  return (
    <div className="space-form-section">
      <div className="menu-section-header">
        <label className="section-label">Select Menus</label>
        <GlassCtaButton
          label="+ Add New Menu"
          variant="secondary"
          onClick={() => {
            setEditingMenu(null);
            setShowNewMenuDialog(true);
          }}
          fullWidth={false}
          style={{
            padding: '4px 12px',
            fontSize: '11px',
            minHeight: '28px'
          }}
        />
      </div>
      
      {errors.menus && <div className="error-text">{errors.menus}</div>}
      
      {availableMenus.length === 0 && newMenus.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">🍽️</span>
          <span>No Menus Created Yet</span>
          <small>Tap 'Add New Menu' to create one</small>
        </div>
      ) : (
        <div className="menu-list">
          {/* Existing Menus */}
          {availableMenus.map(menu => (
            <MenuItem
              key={menu.id}
              menu={menu}
              selected={selectedMenuIds.includes(menu.id)}
              onToggle={onToggleMenu}
              onEdit={handleEditMenu}
              onDelete={handleDeleteMenu}
            />
          ))}
          
          {/* Newly Added Menus (not yet saved) */}
          {newMenus.map((menu, index) => (
            <MenuItem
              key={`new-${index}`}
              menu={menu}
              selected={true}
              onToggle={() => {}}
              isNew={true}
              onEdit={() => handleEditMenu(menu)}
              onDelete={() => {
                setNewMenus(prev => prev.filter((_, i) => i !== index));
              }}
            />
          ))}
        </div>
      )}

      {/* New Menu Dialog */}
      {showNewMenuDialog && (
        <NewMenuDialog
          onClose={() => setShowNewMenuDialog(false)}
          onSave={handleCreateMenu}
          editingMenu={editingMenu}
          cuisineCategories={cuisineCategories}
          currency={currency}
        />
      )}
    </div>
  );
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// New Menu Dialog
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// New Menu Dialog (Fixed)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const NewMenuDialog = ({ onClose, onSave, editingMenu, cuisineCategories, currency }) => {
  const [name, setName] = useState(editingMenu?.name || '');
  const [description, setDescription] = useState(editingMenu?.description || '');
  const [price, setPrice] = useState(editingMenu?.price?.toString() || '');
  const [discountPrice, setDiscountPrice] = useState(editingMenu?.discountPrice?.toString() || '');
  const [categoryId, setCategoryId] = useState(editingMenu?.categoryId || editingMenu?.cuisineCategory?.id || null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = async () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = "Menu name is required";
    if (!price || parseFloat(price) <= 0) newErrors.price = "Valid price is required";
    if (!categoryId) newErrors.category = "Please select Veg or Non-Veg";
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const menuData = {
        name: name.trim(),
        description: description.trim() || undefined,
        price: parseFloat(price),
        categoryId: categoryId,
        ...(discountPrice && discountPrice.trim() && { discountPrice: parseFloat(discountPrice) })
      };
      
      await onSave(menuData);
      onClose();
    } catch (error) {
      console.error("Error saving menu:", error);
      setErrors({ submit: error.message || "Failed to save menu" });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Ensure cuisineCategories is an array and has valid data
  const getCategories = () => {
    // If cuisineCategories is provided and is an array with items
    if (cuisineCategories && Array.isArray(cuisineCategories) && cuisineCategories.length > 0) {
      // Filter out any categories without a name
      const validCategories = cuisineCategories.filter(cat => cat && cat.name);
      if (validCategories.length > 0) {
        return validCategories;
      }
    }
    // Fallback categories
    return [
      { id: 1, name: "Vegetarian" },
      { id: 2, name: "Non-Vegetarian" }
    ];
  };

  const categories = getCategories();

  return (
    <div className="menu-dialog-overlay" onClick={onClose}>
      <div className="menu-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="menu-dialog-header">
          <h3>{editingMenu ? 'Edit Menu' : 'Add New Menu'}</h3>
          <button className="menu-dialog-close" onClick={onClose}>×</button>
        </div>
        <div className="menu-dialog-body">
          <div className="menu-dialog-field">
            <label>Cuisine Type *</label>
            <div className="cuisine-type-grid">
              {categories.map(cat => {
                // Safe check for category name
                const catName = cat?.name || '';
                const isVeg = catName.toLowerCase().includes('veg') && 
                             !catName.toLowerCase().includes('non');
                const isSelected = categoryId === cat.id;
                return (
                  <div
                    key={cat.id || Math.random()}
                    className={`cuisine-type-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => setCategoryId(cat.id)}
                  >
                    <span className={`veg-indicator ${isVeg ? 'veg' : 'non-veg'}`}>
                      {isVeg ? '🟢' : '🔴'}
                    </span>
                    <span>{catName}</span>
                  </div>
                );
              })}
            </div>
            {errors.category && <div className="error-text">{errors.category}</div>}
          </div>

          <div className="menu-dialog-field">
            <GlassInput
              label="Menu Name *"
              placeholder="Enter menu name"
              value={name}
              onChange={setName}
              error={errors.name}
            />
          </div>

          <div className="menu-dialog-field">
            <GlassInput
              label="Description"
              placeholder="Enter description (optional)"
              value={description}
              onChange={setDescription}
              multiline
              rows={2}
            />
          </div>

          <div className="menu-dialog-row">
            <div className="menu-dialog-field">
              <GlassInput
                label={`Price/Person (${currency}) *`}
                placeholder="100"
                type="number"
                value={price}
                onChange={setPrice}
                error={errors.price}
              />
            </div>
            <div className="menu-dialog-field">
              <GlassInput
                label={`After Discount (${currency})`}
                placeholder="80"
                type="number"
                value={discountPrice}
                onChange={setDiscountPrice}
              />
            </div>
          </div>

          {errors.submit && (
            <div className="error-text submit-error">{errors.submit}</div>
          )}

          <div className="menu-dialog-actions">
            <GlassCtaButton
              label="Cancel"
              variant="secondary"
              onClick={onClose}
              fullWidth={false}
              style={{ flex: 1 }}
            />
            <GlassCtaButton
              label={editingMenu ? 'Update Menu' : 'Save Menu'}
              onClick={handleSubmit}
              isLoading={isSubmitting}
              style={{ flex: 2 }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Drink Options Section
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Drink Options Section (Updated)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const DrinkOptionsSection = ({
  drinkOptions,
  setDrinkOptions,
  currency = '₹'
}) => {
  const [showAddDrink, setShowAddDrink] = useState(false);
  const [editingDrinkIndex, setEditingDrinkIndex] = useState(null);

  const handleAddDrink = (drinkData) => {
    setDrinkOptions(prev => [...prev, drinkData]);
    setShowAddDrink(false);
    setEditingDrinkIndex(null);
  };

  const handleEditDrink = (index) => {
    setEditingDrinkIndex(index);
    setShowAddDrink(true);
  };

  const handleDeleteDrink = (index) => {
    if (window.confirm("Delete this drink option?")) {
      setDrinkOptions(prev => prev.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="space-form-section">
      <div className="drink-section-header">
        <label className="section-label">Drink Options</label>
        <GlassCtaButton
          label="+ Add Drink"
          variant="secondary"
          onClick={() => {
            setEditingDrinkIndex(null);
            setShowAddDrink(true);
          }}
          fullWidth={false}
          style={{
            padding: '4px 12px',
            fontSize: '11px',
            minHeight: '28px'
          }}
        />
      </div>

      {drinkOptions.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">🍷</span>
          <span>No drinks added</span>
          <small>Tap 'Add Drink' to add drink packages</small>
        </div>
      ) : (
        <div className="drink-list">
          {drinkOptions.map((drink, index) => {
            // Safely convert prices
            const price = typeof drink.pricePerPerson === 'number' ? drink.pricePerPerson : parseFloat(drink.pricePerPerson) || 0;
            const discountPrice = typeof drink.discountPrice === 'number' ? drink.discountPrice : parseFloat(drink.discountPrice) || null;
            
            return (
              <div key={index} className="drink-item">
                <div className="drink-item-content">
                  <div className="drink-item-header">
                    <span className="drink-item-name">{drink.name}</span>
                    <div className="drink-item-actions">
                      <button 
                        className="drink-item-edit"
                        onClick={() => handleEditDrink(index)}
                      >
                        ✏️
                      </button>
                      <button 
                        className="drink-item-delete"
                        onClick={() => handleDeleteDrink(index)}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  <div className="drink-item-details">
                    <span className="drink-item-price">
                      {currency}{price.toFixed(0)}/person
                    </span>
                    {discountPrice && discountPrice > 0 && (
                      <span className="drink-item-discount">
                        {currency}{discountPrice.toFixed(0)}
                      </span>
                    )}
                    {drink.description && (
                      <span className="drink-item-description">{drink.description}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Drink Dialog */}
      {showAddDrink && (
        <AddDrinkDialog
          onClose={() => {
            setShowAddDrink(false);
            setEditingDrinkIndex(null);
          }}
          onSave={handleAddDrink}
          editingDrink={editingDrinkIndex !== null ? drinkOptions[editingDrinkIndex] : null}
          currency={currency}
        />
      )}
    </div>
  );
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Add Drink Dialog
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Add Drink Dialog (Fixed)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const AddDrinkDialog = ({ onClose, onSave, editingDrink, currency }) => {
  const [name, setName] = useState(editingDrink?.name || '');
  const [description, setDescription] = useState(editingDrink?.description || '');
  const [pricePerPerson, setPricePerPerson] = useState(editingDrink?.pricePerPerson?.toString() || '');
  const [discountPrice, setDiscountPrice] = useState(editingDrink?.discountPrice?.toString() || '');
  const [discountValidUpto, setDiscountValidUpto] = useState(editingDrink?.discountValidUpto || null);
  const [errors, setErrors] = useState({});

  const handleSubmit = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = "Name is required";
    if (!pricePerPerson || parseFloat(pricePerPerson) <= 0) {
      newErrors.price = "Valid price is required";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const drinkData = {
      name: name.trim(),
      description: description.trim() || undefined,
      pricePerPerson: parseFloat(pricePerPerson),
      ...(discountPrice && discountPrice.trim() && { discountPrice: parseFloat(discountPrice) }),
      ...(discountValidUpto && { discountValidUpto })
    };

    onSave(drinkData);
  };

  return (
    <div className="menu-dialog-overlay" onClick={onClose}>
      <div className="menu-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="menu-dialog-header">
          <h3>{editingDrink ? 'Edit Drink Package' : 'Add Drink Package'}</h3>
          <button className="menu-dialog-close" onClick={onClose}>×</button>
        </div>
        <div className="menu-dialog-body">
          <div className="menu-dialog-field">
            <GlassInput
              label="Name *"
              placeholder="Enter drink package name"
              value={name}
              onChange={setName}
              error={errors.name}
            />
          </div>

          <div className="menu-dialog-field">
            <GlassInput
              label="Description"
              placeholder="Enter description (optional)"
              value={description}
              onChange={setDescription}
              multiline
              rows={2}
            />
          </div>

          <div className="menu-dialog-row">
            <div className="menu-dialog-field">
              <GlassInput
                label={`Price/Person (${currency}) *`}
                placeholder="500"
                type="number"
                value={pricePerPerson}
                onChange={setPricePerPerson}
                error={errors.price}
              />
            </div>
            <div className="menu-dialog-field">
              <GlassInput
                label={`After Discount (${currency})`}
                placeholder="400"
                type="number"
                value={discountPrice}
                onChange={setDiscountPrice}
              />
            </div>
          </div>

          <div className="menu-dialog-field">
            <label>Discount Valid Until</label>
            <CustomCalendar
              value={discountValidUpto ? new Date(discountValidUpto) : null}
              onChange={(e) => {
                setDiscountValidUpto(e.value ? e.value.toISOString().split('T')[0] : null);
              }}
              minDate={new Date()}
              placeholder="Select date"
              showIcon={true}
              inputStyle={{
                background: 'var(--glass-tint-ultra)',
                border: '1px solid var(--glass-divider)',
                borderRadius: 'var(--glass-r-sm)',
                padding: 'var(--glass-sp-8) var(--glass-sp-12)',
                fontSize: '14px',
                color: 'var(--glass-text-secondary)',
                fontFamily: '"Inter", sans-serif',
                width: '100%',
                cursor: 'pointer'
              }}
            />
          </div>

          {errors.submit && (
            <div className="error-text submit-error">{errors.submit}</div>
          )}

          <div className="menu-dialog-actions">
            <GlassCtaButton
              label="Cancel"
              variant="secondary"
              onClick={onClose}
              fullWidth={false}
              style={{ flex: 1 }}
            />
            <GlassCtaButton
              label={editingDrink ? 'Update' : 'Add'}
              onClick={handleSubmit}
              style={{ flex: 1 }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Pricing Section
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const PricingSection = ({
  perHourEnabled,
  setPerHourEnabled,
  perHourPrice,
  setPerHourPrice,
  perHourDiscount,
  setPerHourDiscount,
  perHourValidUpto,
  setPerHourValidUpto,
  perDayEnabled,
  setPerDayEnabled,
  perDayPrice,
  setPerDayPrice,
  perDayDiscount,
  setPerDayDiscount,
  perDayValidUpto,
  setPerDayValidUpto,
  perPersonEnabled,
  setPerPersonEnabled,
  perPersonPrice,
  setPerPersonPrice,
  perPersonDiscount,
  setPerPersonDiscount,
  perPersonValidUpto,
  setPerPersonValidUpto,
  foodIncluded,
  setFoodIncluded,
  outsideFoodAllowed,
  setOutsideFoodAllowed,
  availableMenus,
  selectedMenuIds,
  onToggleMenu,
  onCreateMenu,
  onDeleteMenu,
  cuisineCategories,
  drinkOptions,
  setDrinkOptions,
  errors = {},
  currency = '₹'
}) => {
  const renderPricingCard = ({
    title,
    enabled,
    onToggle,
    price,
    setPrice,
    discount,
    setDiscount,
    validUpto,
    setValidUpto,
    priceError,
    discountError,
    dateError,
    extraContent
  }) => (
    <div className={`pricing-card ${enabled ? 'enabled' : ''}`}>
      <div className="pricing-card-header">
        <span className="pricing-card-title">{title}</span>
        <label className="switch">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onToggle(e.target.checked)}
          />
          <span className="slider"></span>
        </label>
      </div>
      
      {enabled && (
        <div className="pricing-card-body">
          <div className="pricing-row">
            <div className="pricing-field">
              <GlassInput
                label={`Original Price (${currency}) *`}
                placeholder="1000"
                type="number"
                value={price}
                onChange={setPrice}
                error={priceError}
                prefix={currency}
              />
            </div>
            <div className="pricing-field">
              <GlassInput
                label={`After Discount (${currency})`}
                placeholder="800"
                type="number"
                value={discount}
                onChange={setDiscount}
                error={discountError}
                prefix={currency}
              />
            </div>
          </div>
          
          <div className="pricing-date">
            <CustomCalendar
              value={validUpto ? new Date(validUpto) : null}
              onChange={(e) => {
                setValidUpto(e.value ? e.value.toISOString().split('T')[0] : null);
              }}
              minDate={new Date()}
              placeholder="Discount valid until (optional)"
              showIcon={true}
              inputStyle={{
                background: 'var(--glass-tint-ultra)',
                border: '1px solid var(--glass-divider)',
                borderRadius: 'var(--glass-r-sm)',
                padding: 'var(--glass-sp-8) var(--glass-sp-12)',
                fontSize: '12px',
                color: 'var(--glass-text-secondary)',
                fontFamily: '"Inter", sans-serif'
              }}
            />
            {dateError && <div className="error-text">{dateError}</div>}
          </div>
          
          {extraContent}
        </div>
      )}
    </div>
  );

  const renderPerPersonExtras = () => (
    <div className="per-person-extras">
      <div className="toggle-option">
        <label className="toggle-label">
          <div className="toggle-label-content">
            <input
              type="checkbox"
              checked={foodIncluded}
              onChange={(e) => setFoodIncluded(e.target.checked)}
            />
            <div>
              <span className="toggle-title">Food Included</span>
              <span className="toggle-subtitle">Menu price is the per-person charge</span>
            </div>
          </div>
        </label>
      </div>
      
      <div className="toggle-option">
        <label className="toggle-label">
          <div className="toggle-label-content">
            <input
              type="checkbox"
              checked={outsideFoodAllowed}
              onChange={(e) => setOutsideFoodAllowed(e.target.checked)}
            />
            <div>
              <span className="toggle-title">Outside Food Allowed</span>
              {foodIncluded && (
                <span className="toggle-subtitle">Price field = base venue charge without food</span>
              )}
            </div>
          </div>
        </label>
      </div>
      
      {(!foodIncluded || outsideFoodAllowed) && perPersonPrice && (
        <div className="info-box">
          <span className="info-icon">ℹ️</span>
          <span>Price will be charged as base venue charge per person</span>
        </div>
      )}
      
      {foodIncluded && !outsideFoodAllowed && !perPersonPrice && (
        <div className="info-box">
          <span className="info-icon">ℹ️</span>
          <span>Menu price will be the per-person charge</span>
        </div>
      )}
      
      {/* Menu Selection when Food Included */}
      {foodIncluded && (
        <div className="menu-selection-wrapper">
          <MenuSelectionSection
            availableMenus={availableMenus}
            selectedMenuIds={selectedMenuIds}
            onToggleMenu={onToggleMenu}
            onCreateMenu={onCreateMenu}
            onDeleteMenu={onDeleteMenu}
            cuisineCategories={cuisineCategories}
            currency={currency}
            errors={errors}
          />
        </div>
      )}
      
      {/* Drink Options */}
      <div className="drink-selection-wrapper">
        <DrinkOptionsSection
          drinkOptions={drinkOptions}
          setDrinkOptions={setDrinkOptions}
          currency={currency}
        />
      </div>
    </div>
  );

  return (
    <div className="space-form-section">
      <label className="section-label">Pricing</label>
      <p className="section-subtitle">Enable at least one pricing type</p>
      {errors.pricing && <div className="error-text">{errors.pricing}</div>}
      
      {renderPricingCard({
        title: 'Per Hour',
        enabled: perHourEnabled,
        onToggle: setPerHourEnabled,
        price: perHourPrice,
        setPrice: setPerHourPrice,
        discount: perHourDiscount,
        setDiscount: setPerHourDiscount,
        validUpto: perHourValidUpto,
        setValidUpto: setPerHourValidUpto,
        priceError: errors.perHourPrice,
        discountError: errors.perHourDiscount,
        dateError: errors.perHourDate,
      })}
      
      {renderPricingCard({
        title: 'Per Day',
        enabled: perDayEnabled,
        onToggle: setPerDayEnabled,
        price: perDayPrice,
        setPrice: setPerDayPrice,
        discount: perDayDiscount,
        setDiscount: setPerDayDiscount,
        validUpto: perDayValidUpto,
        setValidUpto: setPerDayValidUpto,
        priceError: errors.perDayPrice,
        discountError: errors.perDayDiscount,
        dateError: errors.perDayDate,
      })}
      
      {renderPricingCard({
        title: 'Per Person',
        enabled: perPersonEnabled,
        onToggle: setPerPersonEnabled,
        price: perPersonPrice,
        setPrice: setPerPersonPrice,
        discount: perPersonDiscount,
        setDiscount: setPerPersonDiscount,
        validUpto: perPersonValidUpto,
        setValidUpto: setPerPersonValidUpto,
        priceError: errors.perPersonPrice,
        discountError: errors.perPersonDiscount,
        dateError: errors.perPersonDate,
        extraContent: perPersonEnabled && renderPerPersonExtras()
      })}
    </div>
  );
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Features Section
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const FeaturesSection = ({
  availableFeatures,
  selectedFeatureIds,
  onToggleFeature,
  isLoading = false
}) => {
  const [expanded, setExpanded] = useState(false);

  if (isLoading) {
    return (
      <div className="space-form-section">
        <label className="section-label">Features</label>
        <div className="loading-features">
          <GlassSpinner size={20} />
          <span>Loading features...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-form-section">
      <div className="features-header" onClick={() => setExpanded(!expanded)}>
        <div>
          <label className="section-label">Features</label>
          <span className="features-count">{selectedFeatureIds.length} selected</span>
        </div>
        <span className={`expand-arrow ${expanded ? 'rotated' : ''}`}>▼</span>
      </div>
      
      {expanded && (
        <div className="features-grid">
          {availableFeatures.length === 0 ? (
            <div className="empty-state">No features available for this type</div>
          ) : (
            <>
              <div className="select-all-row">
                <label className="select-all-label">
                  <input
                    type="checkbox"
                    checked={selectedFeatureIds.length === availableFeatures.length}
                    onChange={() => {
                      if (selectedFeatureIds.length === availableFeatures.length) {
                        availableFeatures.forEach(f => {
                          if (selectedFeatureIds.includes(f.id)) {
                            onToggleFeature(f.id);
                          }
                        });
                      } else {
                        availableFeatures.forEach(f => {
                          if (!selectedFeatureIds.includes(f.id)) {
                            onToggleFeature(f.id);
                          }
                        });
                      }
                    }}
                  />
                  <span>
                    {selectedFeatureIds.length === availableFeatures.length 
                      ? 'Deselect All' 
                      : `Select All (${availableFeatures.length})`
                    }
                  </span>
                </label>
              </div>
              <div className="features-divider" />
              {availableFeatures.map(feature => (
                <label key={feature.id} className="feature-item">
                  <input
                    type="checkbox"
                    checked={selectedFeatureIds.includes(feature.id)}
                    onChange={() => onToggleFeature(feature.id)}
                  />
                  <span className="feature-name">{feature.name}</span>
                  {feature.description && (
                    <GlassTooltip content={feature.description}>
                      <span className="info-icon">ⓘ</span>
                    </GlassTooltip>
                  )}
                </label>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Rules Section
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const RulesSection = ({
  allRules,
  selectedRuleIds,
  onToggleRule,
  errors = {}
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="space-form-section">
      <div className="rules-header" onClick={() => setExpanded(!expanded)}>
        <div>
          <label className="section-label">Rules</label>
          <span className="rules-count">{selectedRuleIds.length} selected</span>
        </div>
        <span className={`expand-arrow ${expanded ? 'rotated' : ''}`}>▼</span>
      </div>
      
      {expanded && (
        <div className="rules-grid">
          {allRules.length === 0 ? (
            <div className="empty-state">No rules available</div>
          ) : (
            <>
              <div className="select-all-row">
                <label className="select-all-label">
                  <input
                    type="checkbox"
                    checked={selectedRuleIds.length === allRules.length}
                    onChange={() => {
                      if (selectedRuleIds.length === allRules.length) {
                        allRules.forEach(r => {
                          if (selectedRuleIds.includes(r.id)) {
                            onToggleRule(r.id);
                          }
                        });
                      } else {
                        allRules.forEach(r => {
                          if (!selectedRuleIds.includes(r.id)) {
                            onToggleRule(r.id);
                          }
                        });
                      }
                    }}
                  />
                  <span>
                    {selectedRuleIds.length === allRules.length 
                      ? 'Deselect All' 
                      : `Select All (${allRules.length})`
                    }
                  </span>
                </label>
              </div>
              <div className="rules-divider" />
              {allRules.map(rule => (
                <label key={rule.id} className="rule-item">
                  <input
                    type="checkbox"
                    checked={selectedRuleIds.includes(rule.id)}
                    onChange={() => onToggleRule(rule.id)}
                  />
                  <span className="rule-name">{rule.name}</span>
                  {rule.description && (
                    <GlassTooltip content={rule.description}>
                      <span className="info-icon">ⓘ</span>
                    </GlassTooltip>
                  )}
                </label>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Suitable Events Section
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const SuitableEventsSection = ({
  allEvents,
  selectedEventIds,
  onToggleEvent
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="space-form-section">
      <div className="rules-header" onClick={() => setExpanded(!expanded)}>
        <div>
          <label className="section-label">Suitable Events</label>
          <span className="rules-count">{selectedEventIds.length} selected</span>
        </div>
        <span className={`expand-arrow ${expanded ? 'rotated' : ''}`}>▼</span>
      </div>
      
      {expanded && (
        <div className="rules-grid">
          {allEvents.length === 0 ? (
            <div className="empty-state">No events available</div>
          ) : (
            <>
              <div className="select-all-row">
                <label className="select-all-label">
                  <input
                    type="checkbox"
                    checked={selectedEventIds.length === allEvents.length}
                    onChange={() => {
                      if (selectedEventIds.length === allEvents.length) {
                        allEvents.forEach(e => {
                          if (selectedEventIds.includes(e.id)) {
                            onToggleEvent(e.id);
                          }
                        });
                      } else {
                        allEvents.forEach(e => {
                          if (!selectedEventIds.includes(e.id)) {
                            onToggleEvent(e.id);
                          }
                        });
                      }
                    }}
                  />
                  <span>
                    {selectedEventIds.length === allEvents.length 
                      ? 'Deselect All' 
                      : `Select All (${allEvents.length})`
                    }
                  </span>
                </label>
              </div>
              <div className="rules-divider" />
              {allEvents.map(event => (
                <label key={event.id} className="rule-item">
                  <input
                    type="checkbox"
                    checked={selectedEventIds.includes(event.id)}
                    onChange={() => onToggleEvent(event.id)}
                  />
                  <span className="rule-name">{event.name}</span>
                </label>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Main VenueSpaceForm Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export const VenueSpaceForm = ({
  venue,
  editingSpace,
  onClose,
  onSubmit,
  allSpaceTypes,
  allRules,
  allFeatures,
  allCuisineCategories,
  allMenus,
  onCreateMenu,
  onDeleteMenu,
  isOpen = true
}) => {
  // ─── State ──────────────────────────────────────────────────────
  
  const [currentPage, setCurrentPage] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingFeatures, setIsLoadingFeatures] = useState(false);
  
  // Basic Info
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  
  // Capacity & Area
  const [minCapacity, setMinCapacity] = useState('');
  const [maxCapacity, setMaxCapacity] = useState('');
  const [areaSqft, setAreaSqft] = useState('');
  
  // Images
  const [uploadedImageIds, setUploadedImageIds] = useState([]);
  const [existingImageIds, setExistingImageIds] = useState([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  
  // Rules & Features
  const [selectedRuleIds, setSelectedRuleIds] = useState([]);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState([]);
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [availableRules, setAvailableRules] = useState([]);

  // Suitable Events
  const [allEvents, setAllEvents] = useState([]);
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  
  // Pricing - Per Hour
  const [perHourEnabled, setPerHourEnabled] = useState(false);
  const [perHourPrice, setPerHourPrice] = useState('');
  const [perHourDiscount, setPerHourDiscount] = useState('');
  const [perHourValidUpto, setPerHourValidUpto] = useState(null);
  
  // Pricing - Per Day
  const [perDayEnabled, setPerDayEnabled] = useState(false);
  const [perDayPrice, setPerDayPrice] = useState('');
  const [perDayDiscount, setPerDayDiscount] = useState('');
  const [perDayValidUpto, setPerDayValidUpto] = useState(null);
  
  // Pricing - Per Person
  const [perPersonEnabled, setPerPersonEnabled] = useState(false);
  const [perPersonPrice, setPerPersonPrice] = useState('');
  const [perPersonDiscount, setPerPersonDiscount] = useState('');
  const [perPersonValidUpto, setPerPersonValidUpto] = useState(null);
  const [foodIncluded, setFoodIncluded] = useState(true);
  const [outsideFoodAllowed, setOutsideFoodAllowed] = useState(false);
  
  // Menus
  const [selectedMenuIds, setSelectedMenuIds] = useState([]);
  
  // Drink Options
  const [drinkOptions, setDrinkOptions] = useState([]);

  // Terms Document
  const [termsFileId, setTermsFileId] = useState(null);
  const [contractFileId, setContractFileId] = useState(null);
  const [isUploadingContract, setIsUploadingContract] = useState(false);
  const [isUploadingTerms, setIsUploadingTerms] = useState(false);
  
  // Errors
  const [errors, setErrors] = useState({});
  
  const totalPages = 3;
  const isEditing = !!editingSpace;
  const currency = '₹';

  // ─── Load Existing Data ────────────────────────────────────────
  
  useEffect(() => {
    if (editingSpace) {
      setName(editingSpace.name || '');
      setDescription(editingSpace.description || '');
      setSelectedTypeId(editingSpace.spaceTypeId || null);
      setMinCapacity(String(editingSpace.minCapacity || ''));
      setMaxCapacity(String(editingSpace.maxCapacity || ''));
      setAreaSqft(String(editingSpace.areaSqft || ''));
      
      const imageIds = (editingSpace.images || [])
        .map(img => {
          const urlParts = img.url?.split('/');
          return urlParts?.[urlParts.length - 1] || '';
        })
        .filter(id => id);
      setExistingImageIds(imageIds);
      
      setSelectedRuleIds((editingSpace.rules || []).map(r => r.id));
      setSelectedFeatureIds((editingSpace.features || []).map(f => f.id));
      setSelectedMenuIds((editingSpace.menus || []).map(m => m.id).filter(id => id));
      
      // Pricing
      (editingSpace.pricing || []).forEach(p => {
        switch (p.type) {
          case 'per_hour':
            setPerHourEnabled(true);
            setPerHourPrice(String(p.price || ''));
            setPerHourDiscount(String(p.discountPrice || ''));
            setPerHourValidUpto(p.discountValidUpto || null);
            break;
          case 'per_day':
            setPerDayEnabled(true);
            setPerDayPrice(String(p.price || ''));
            setPerDayDiscount(String(p.discountPrice || ''));
            setPerDayValidUpto(p.discountValidUpto || null);
            break;
          case 'per_person':
            setPerPersonEnabled(true);
            setPerPersonPrice(String(p.price || ''));
            setPerPersonDiscount(String(p.discountPrice || ''));
            setPerPersonValidUpto(p.discountValidUpto || null);
            setFoodIncluded(p.foodIncluded !== undefined ? p.foodIncluded : true);
            setOutsideFoodAllowed(p.outsideFoodAllowed || false);
            break;
        }
      });
      
      // Drink Options
      setDrinkOptions(editingSpace.drinkOptions || []);

      // Terms Document
      setTermsFileId(editingSpace.termsFileId || null);
      setContractFileId(editingSpace.contractFileId || null);

      // Selected Events
      setSelectedEventIds(editingSpace.eventIds || (editingSpace.events || []).map(e => e.id || e));
    }
  }, [editingSpace]);

  // ─── Load Features and Rules when space type changes ──────────
  
  useEffect(() => {
    const loadTypeSpecificData = async () => {
      if (!selectedTypeId) {
        setAvailableFeatures([]);
        setAvailableRules([]);
        return;
      }
      setIsLoadingFeatures(true);
      try {
        const [features, rules] = await Promise.all([
          fetchSpaceFeatures(selectedTypeId),
          fetchSpaceRules(selectedTypeId)
        ]);
        setAvailableFeatures(features || []);
        setAvailableRules(rules || []);
      } catch (err) {
        console.error("Error loading space type features/rules:", err);
      } finally {
        setIsLoadingFeatures(false);
      }
    };
    loadTypeSpecificData();
  }, [selectedTypeId]);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const data = await fetchCategoryEvents();
        if (data) {
          setAllEvents(data || []);
        }
      } catch (err) {
        console.error("Error loading category events:", err);
      }
    };
    loadEvents();
  }, []);

  // ─── Validation ────────────────────────────────────────────────
  
  const validatePage = useCallback(() => {
    const newErrors = {};
    
    switch (currentPage) {
      case 0:
        if (!name.trim()) newErrors.name = "Space name is required";
        if (!selectedTypeId) newErrors.spaceType = "Please select a space type";
        break;
      case 1:
        if (!minCapacity) newErrors.minCapacity = "Required";
        if (!maxCapacity) newErrors.maxCapacity = "Required";
        if (minCapacity && maxCapacity && 
            parseInt(minCapacity) > parseInt(maxCapacity)) {
          newErrors.maxCapacity = "Must be >= min capacity";
        }
        if (!areaSqft) newErrors.areaSqft = "Area is required";
        break;
      case 2:
        if (!perHourEnabled && !perDayEnabled && !perPersonEnabled) {
          newErrors.pricing = "Enable at least one pricing type";
        }
        if (perHourEnabled && !perHourPrice) {
          newErrors.perHourPrice = "Price is required";
        }
        if (perDayEnabled && !perDayPrice) {
          newErrors.perDayPrice = "Price is required";
        }
        if (perPersonEnabled && !perPersonPrice) {
          newErrors.perPersonPrice = "Price is required";
        }
        if (perPersonEnabled && foodIncluded && selectedMenuIds.length === 0) {
          newErrors.menus = "Please select at least one menu";
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [currentPage, name, selectedTypeId, minCapacity, maxCapacity, areaSqft,
      perHourEnabled, perHourPrice, perDayEnabled, perDayPrice, 
      perPersonEnabled, perPersonPrice, foodIncluded, selectedMenuIds]);

  // ─── Handlers ──────────────────────────────────────────────────
  
  const handleNext = useCallback(() => {
    if (validatePage()) {
      setCurrentPage(prev => prev + 1);
    }
  }, [validatePage]);

  const handlePrevious = useCallback(() => {
    setCurrentPage(prev => prev - 1);
  }, []);

  const handleImageUpload = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploadingImages(true);
    const newIds = [];
    for (const file of files) {
      try {
        const id = await uploadImage(file);
        if (id) newIds.push(id);
      } catch (err) {
        console.error("Error uploading image:", err);
      }
    }
    setUploadedImageIds(prev => [...prev, ...newIds]);
    setIsUploadingImages(false);
    e.target.value = null;
  }, []);

  const removeImage = useCallback((id, isExisting) => {
    if (isExisting) {
      setExistingImageIds(prev => prev.filter(imgId => imgId !== id));
    } else {
      setUploadedImageIds(prev => prev.filter(imgId => imgId !== id));
    }
  }, []);

  const handleTermsUpload = useCallback(async () => {
    if (termsFileId) {
      setTermsFileId(null);
      return;
    }
    
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.pdf,.jpg,.jpeg,.png';
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        setIsUploadingTerms(true);
        try {
          const id = await uploadImage(file);
          if (id) {
            setTermsFileId(id);
          }
        } catch (err) {
          console.error("Error uploading terms document:", err);
          alert("Failed to upload document. Please try again.");
        } finally {
          setIsUploadingTerms(false);
        }
      };
      input.click();
    } catch (err) {
      console.error("Error picking terms document:", err);
    }
  }, [termsFileId]);

  const handleToggleFeature = useCallback((featureId) => {
    setSelectedFeatureIds(prev => 
      prev.includes(featureId)
        ? prev.filter(id => id !== featureId)
        : [...prev, featureId]
    );
  }, []);

  const handleToggleRule = useCallback((ruleId) => {
    setSelectedRuleIds(prev => 
      prev.includes(ruleId)
        ? prev.filter(id => id !== ruleId)
        : [...prev, ruleId]
    );
  }, []);

  const handleToggleEvent = useCallback((eventId) => {
    setSelectedEventIds(prev => 
      prev.includes(eventId)
        ? prev.filter(id => id !== eventId)
        : [...prev, eventId]
    );
  }, []);



  const handleToggleMenu = useCallback((menuId) => {
    setSelectedMenuIds(prev => 
      prev.includes(menuId)
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  }, []);

  const handleCreateMenu = useCallback(async (menuData) => {
    // Add the new menu to the available menus list and get returned object
    const result = await onCreateMenu(menuData);
    // Auto-select the new menu
    if (result && result.id) {
      setSelectedMenuIds(prev => [...prev, result.id]);
    }
    return result;
  }, [onCreateMenu]);

  const handleSubmit = useCallback(async () => {
    if (isUploadingContract) return;
    // Validate all pages
    const allErrors = {};
    if (!name.trim()) allErrors.name = "Space name is required";
    if (!selectedTypeId) allErrors.spaceType = "Please select a space type";
    if (!minCapacity) allErrors.minCapacity = "Required";
    if (!maxCapacity) allErrors.maxCapacity = "Required";
    if (minCapacity && maxCapacity && 
        parseInt(minCapacity) > parseInt(maxCapacity)) {
      allErrors.maxCapacity = "Must be >= min capacity";
    }
    if (!areaSqft) allErrors.areaSqft = "Area is required";
    if (!perHourEnabled && !perDayEnabled && !perPersonEnabled) {
      allErrors.pricing = "Enable at least one pricing type";
    }
    if (perHourEnabled && !perHourPrice) {
      allErrors.perHourPrice = "Price is required";
    }
    if (perDayEnabled && !perDayPrice) {
      allErrors.perDayPrice = "Price is required";
    }
    if (perPersonEnabled && !perPersonPrice) {
      allErrors.perPersonPrice = "Price is required";
    }
    if (perPersonEnabled && foodIncluded && selectedMenuIds.length === 0) {
      allErrors.menus = "Please select at least one menu";
    }
    
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      return;
    }

    setIsSubmitting(true);
    
    try {
      const allImageIds = [...existingImageIds, ...uploadedImageIds];
      const pricing = [];
      
      if (perHourEnabled && perHourPrice) {
        const p = {
          type: 'per_hour',
          price: parseFloat(perHourPrice),
        };
        if (perHourDiscount) p.discountPrice = parseFloat(perHourDiscount);
        if (perHourValidUpto) p.discountValidUpto = perHourValidUpto;
        pricing.push(p);
      }
      
      if (perDayEnabled && perDayPrice) {
        const p = {
          type: 'per_day',
          price: parseFloat(perDayPrice),
        };
        if (perDayDiscount) p.discountPrice = parseFloat(perDayDiscount);
        if (perDayValidUpto) p.discountValidUpto = perDayValidUpto;
        pricing.push(p);
      }
      
      if (perPersonEnabled) {
        const p = {
          type: 'per_person',
          foodIncluded: foodIncluded,
          outsideFoodAllowed: outsideFoodAllowed,
        };
        if (perPersonPrice) p.price = parseFloat(perPersonPrice);
        if (perPersonDiscount) p.discountPrice = parseFloat(perPersonDiscount);
        if (perPersonValidUpto) p.discountValidUpto = perPersonValidUpto;
        if (foodIncluded && selectedMenuIds.length > 0) {
          p.menuIds = selectedMenuIds;
        }
        pricing.push(p);
      }
      
      const payload = {
        name: name.trim(),
        spaceTypeId: selectedTypeId,
        minCapacity: parseInt(minCapacity),
        maxCapacity: parseInt(maxCapacity),
        areaSqft: parseFloat(areaSqft),
        imageIds: allImageIds,
        ruleIds: selectedRuleIds,
        featureIds: selectedFeatureIds,
        eventIds: selectedEventIds,
        pricing: pricing,
        drinkOptions: drinkOptions,
        termsFileId: termsFileId || undefined,
        contractFileId,
      };
      
      if (description.trim()) payload.description = description.trim();
      
      if (isEditing && editingSpace.id) {
        await updateSpace(venue.id, editingSpace.id, payload);
      } else {
        await createSpace(venue.id, payload);
      }
      
      onSubmit?.();
    } catch (err) {
      console.error("Error submitting space:", err);
      setErrors({ submit: err.message || "Failed to save space" });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    name, description, selectedTypeId, minCapacity, maxCapacity, areaSqft,
    existingImageIds, uploadedImageIds, selectedRuleIds, selectedFeatureIds, selectedEventIds,
    perHourEnabled, perHourPrice, perHourDiscount, perHourValidUpto,
    perDayEnabled, perDayPrice, perDayDiscount, perDayValidUpto,
    perPersonEnabled, perPersonPrice, perPersonDiscount, perPersonValidUpto,
    foodIncluded, outsideFoodAllowed, selectedMenuIds, drinkOptions,
    termsFileId, contractFileId, isUploadingContract,
    venue, editingSpace, isEditing, onSubmit
  ]);

  // ─── Render ────────────────────────────────────────────────────
  
  const renderStep1 = () => (
    <>
      <div className="space-form-section">
        <GlassInput
          label="Space Name *"
          placeholder="e.g. Grand Banquet Hall"
          value={name}
          onChange={setName}
          error={errors.name}
        />
      </div>
      
      <div className="space-form-section">
        <GlassInput
          label="Description"
          placeholder="Describe this space..."
          value={description}
          onChange={setDescription}
          multiline
          rows={3}
        />
      </div>
      
      <SpaceTypeSection
        spaceTypes={allSpaceTypes}
        selectedTypeId={selectedTypeId}
        onSelect={setSelectedTypeId}
        errors={errors}
      />
    </>
  );

  const renderStep2 = () => (
    <PricingSection
      perHourEnabled={perHourEnabled}
      setPerHourEnabled={setPerHourEnabled}
      perHourPrice={perHourPrice}
      setPerHourPrice={setPerHourPrice}
      perHourDiscount={perHourDiscount}
      setPerHourDiscount={setPerHourDiscount}
      perHourValidUpto={perHourValidUpto}
      setPerHourValidUpto={setPerHourValidUpto}
      perDayEnabled={perDayEnabled}
      setPerDayEnabled={setPerDayEnabled}
      perDayPrice={perDayPrice}
      setPerDayPrice={setPerDayPrice}
      perDayDiscount={perDayDiscount}
      setPerDayDiscount={setPerDayDiscount}
      perDayValidUpto={perDayValidUpto}
      setPerDayValidUpto={setPerDayValidUpto}
      perPersonEnabled={perPersonEnabled}
      setPerPersonEnabled={setPerPersonEnabled}
      perPersonPrice={perPersonPrice}
      setPerPersonPrice={setPerPersonPrice}
      perPersonDiscount={perPersonDiscount}
      setPerPersonDiscount={setPerPersonDiscount}
      perPersonValidUpto={perPersonValidUpto}
      setPerPersonValidUpto={setPerPersonValidUpto}
      foodIncluded={foodIncluded}
      setFoodIncluded={setFoodIncluded}
      outsideFoodAllowed={outsideFoodAllowed}
      setOutsideFoodAllowed={setOutsideFoodAllowed}
      availableMenus={allMenus}
      selectedMenuIds={selectedMenuIds}
      onToggleMenu={handleToggleMenu}
      onCreateMenu={handleCreateMenu}
      onDeleteMenu={onDeleteMenu}
      cuisineCategories={allCuisineCategories}
      drinkOptions={drinkOptions}
      setDrinkOptions={setDrinkOptions}
      errors={errors}
      currency={currency}
    />
  );

  const renderStep3 = () => (
    <>
      <CapacityAreaSection
        minCapacity={minCapacity}
        setMinCapacity={setMinCapacity}
        maxCapacity={maxCapacity}
        setMaxCapacity={setMaxCapacity}
        areaSqft={areaSqft}
        setAreaSqft={setAreaSqft}
        errors={errors}
      />
      
      <ImageUploadSection
        existingImageIds={existingImageIds}
        uploadedImageIds={uploadedImageIds}
        onImageUpload={handleImageUpload}
        onRemoveImage={removeImage}
        isUploading={isUploadingImages}
        label="Space Images"
      />

      <div className="space-form-section" style={{ marginTop: '16px' }}>
        <OptionalContractUpload fileId={contractFileId} onChange={setContractFileId} onUploadingChange={setIsUploadingContract} />
        <label className="section-label">Terms Document</label>
        <p className="section-subtitle" style={{ marginTop: '-4px', marginBottom: '12px' }}>
          Upload venue terms & conditions (optional)
        </p>
        {termsFileId ? (
          <div className="pdf-uploaded" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', border: '1px solid var(--glass-divider)', borderRadius: 'var(--glass-r-sm)', background: 'rgba(52, 199, 89, 0.04)' }}>
            <span style={{ fontSize: '13px', color: 'var(--glass-success, #34c759)' }}>
              📄 Document Uploaded
            </span>
            <button 
              type="button" 
              onClick={handleTermsUpload} 
              className="pdf-remove-btn"
              style={{ padding: '4px 8px', fontSize: '12px', border: 'none', background: 'none', color: 'var(--glass-error)', cursor: 'pointer' }}
            >
              Remove
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="document-upload-button"
            onClick={handleTermsUpload}
            disabled={isUploadingTerms}
          >
            {isUploadingTerms ? 'Uploading...' : 'Upload PDF / JPG'}
          </button>
        )}
      </div>

      <RulesSection
        allRules={availableRules}
        selectedRuleIds={selectedRuleIds}
        onToggleRule={handleToggleRule}
        errors={errors}
      />

      <SuitableEventsSection
        allEvents={allEvents}
        selectedEventIds={selectedEventIds}
        onToggleEvent={handleToggleEvent}
      />

      <FeaturesSection
        availableFeatures={availableFeatures}
        selectedFeatureIds={selectedFeatureIds}
        onToggleFeature={handleToggleFeature}
        isLoading={isLoadingFeatures}
      />
    </>
  );

  return (
    <div className="venue-space-form-overlay" onClick={onClose}>
      <div className="venue-space-form-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="venue-space-form-header">
          <h2>{isEditing ? 'Edit Space' : 'Add Space'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="venue-space-form-body">
          <GlassPageIndicator currentPage={currentPage} totalPages={totalPages} />
          
          <div className="venue-space-form-content">
            {currentPage === 0 && renderStep1()}
            {currentPage === 1 && renderStep3()}
            {currentPage === 2 && renderStep2()}
          </div>
          
          {errors.submit && (
            <div className="error-text submit-error">{errors.submit}</div>
          )}
          
          <div className="venue-space-form-footer">
            {currentPage > 0 && (
              <GlassCtaButton
                label="Previous"
                variant="secondary"
                onClick={handlePrevious}
                fullWidth={false}
                style={{ flex: 1 }}
              />
            )}
            {currentPage > 0 && <div className="nav-spacer" />}
            <GlassCtaButton
              label={currentPage === totalPages - 1 
                ? (isEditing ? 'Update Space' : 'Create Space')
                : 'Next'
              }
              onClick={currentPage < totalPages - 1 ? handleNext : handleSubmit}
              isLoading={isSubmitting}
              disabled={isSubmitting || isUploadingContract}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VenueSpaceForm;
